import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, CheckCircle2, Inbox, Mic, XCircle } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import { dateTime } from '@/lib/format';
import { aSlug } from '@/lib/slug';
import { marcadosAlAbrir, modulosParaCliente, ordenarSolicitudes } from '@/lib/solicitudes';
import * as api from '@/api/negocios-cliente.api';
import type {
  EstadoSolicitud,
  ModuloDelCatalogo,
  SolicitudDetalle,
  SolicitudResumen,
} from '@/api/negocios-cliente.api';

/**
 * SOLICITUDES DE ALTA — los clientes que se registraron solos.
 *
 * El OWNER escucha sus audios, lee lo que escribieron y lo que propuso Claude,
 * ajusta los módulos y aprueba (nace el negocio) o rechaza con un motivo.
 * Quien decide qué se puede es la API: aquí solo se ofrece lo que tiene sentido.
 */

const ESTADO: Record<EstadoSolicitud, { etiqueta: string; variant: BadgeProps['variant'] }> = {
  BORRADOR: { etiqueta: 'Borrador', variant: 'default' },
  ENVIADA: { etiqueta: 'Por revisar', variant: 'warning' },
  APROBADA: { etiqueta: 'Aprobada', variant: 'success' },
  RECHAZADA: { etiqueta: 'Rechazada', variant: 'danger' },
};

const QK = ['operacion', 'solicitudes'] as const;
const QK_CATALOGO = ['modulos', 'catalogo'] as const;

export default function Solicitudes() {
  const [elegida, setElegida] = useState<string | null>(null);
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: QK, queryFn: api.listarSolicitudes });
  const lista = ordenarSolicitudes(data ?? []);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Solicitudes de alta"
        description="Los clientes que se registraron solos. Escúchalos, ajusta los módulos y aprueba o rechaza."
      />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size={28} className="text-brand-600" />
        </div>
      ) : isError ? (
        <EmptyState
          icon={AlertTriangle}
          title="No se pudo cargar la lista"
          description={(error as Error).message}
          action={
            <Button variant="outline" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      ) : lista.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No hay solicitudes"
          description="Cuando alguien se registre y envíe su solicitud, aparece aquí."
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <Card className={cn('h-fit overflow-hidden', elegida && 'hidden lg:block')}>
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {lista.map((s) => (
                <FilaSolicitud key={s.id} s={s} activa={s.id === elegida} onClick={() => setElegida(s.id)} />
              ))}
            </ul>
          </Card>
          {elegida ? (
            <Detalle key={elegida} id={elegida} onVolver={() => setElegida(null)} />
          ) : (
            <Card className="hidden p-8 text-center text-sm text-slate-500 lg:block">
              Elige una solicitud para verla.
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function FilaSolicitud({ s, activa, onClick }: { s: SolicitudResumen; activa: boolean; onClick: () => void }) {
  const estado = ESTADO[s.estado] ?? ESTADO.ENVIADA;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'w-full space-y-1 p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50',
          activa && 'bg-brand-50 dark:bg-brand-900/20',
        )}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate font-semibold text-slate-900 dark:text-slate-100">{s.negocioNombre}</span>
          <Badge variant={estado.variant}>{estado.etiqueta}</Badge>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
          <span className="truncate">{s.nombre ?? s.email}</span>
          <span>{dateTime(s.updatedAt)}</span>
          {s.audios > 0 && (
            <span className="inline-flex items-center gap-1">
              <Mic className="h-3 w-3" /> {s.audios}
            </span>
          )}
        </div>
      </button>
    </li>
  );
}

function Detalle({ id, onVolver }: { id: string; onVolver: () => void }) {
  const sol = useQuery({ queryKey: ['operacion', 'solicitud', id], queryFn: () => api.verSolicitud(id) });
  const cat = useQuery({ queryKey: QK_CATALOGO, queryFn: api.catalogoDeModulos, staleTime: 5 * 60_000 });

  return (
    <Card className="space-y-5 p-4 sm:p-5">
      <Button variant="ghost" size="sm" className="lg:hidden" onClick={onVolver}>
        <ArrowLeft className="h-4 w-4" /> Volver a la lista
      </Button>
      {sol.isLoading || cat.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner size={24} className="text-brand-600" />
        </div>
      ) : sol.isError || cat.isError ? (
        <EmptyState
          icon={AlertTriangle}
          title="No se pudo abrir la solicitud"
          description={((sol.error ?? cat.error) as Error).message}
          action={
            <Button variant="outline" onClick={() => (sol.isError ? sol.refetch() : cat.refetch())}>
              Reintentar
            </Button>
          }
        />
      ) : sol.data && cat.data ? (
        <Contenido s={sol.data} catalogo={cat.data} />
      ) : null}
    </Card>
  );
}

function Contenido({ s, catalogo }: { s: SolicitudDetalle; catalogo: ModuloDelCatalogo[] }) {
  const nombres = Object.fromEntries(catalogo.map((m) => [m.id, m.nombre]));
  const estado = ESTADO[s.estado] ?? ESTADO.ENVIADA;
  return (
    <>
      <header className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{s.negocioNombre}</h2>
          <Badge variant={estado.variant}>{estado.etiqueta}</Badge>
        </div>
        <p className="break-all text-sm text-slate-500 dark:text-slate-400">
          {s.nombre ? `${s.nombre} · ` : ''}
          {s.email} · {dateTime(s.updatedAt)}
        </p>
      </header>

      <Seccion titulo={`Audios (${s.audios.length})`}>
        {s.audios.length === 0 ? (
          <p className="text-sm text-slate-500">No mandó audios.</p>
        ) : (
          <ol className="space-y-3">
            {s.audios.map((a, n) => (
              <li key={a.url} className="space-y-1.5 rounded-md border border-surface-border p-3 dark:border-slate-800">
                <p className="text-xs text-slate-500">
                  Audio {n + 1} · {dateTime(a.creadoEn)}
                </p>
                <Reproductor url={a.url} />
                <p className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">
                  {a.transcripcion ?? <span className="italic text-slate-400">Sin transcripción: escúchalo.</span>}
                </p>
              </li>
            ))}
          </ol>
        )}
      </Seccion>

      <Seccion titulo="Lo que escribió">
        {s.textoCliente.trim() ? (
          <p className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{s.textoCliente}</p>
        ) : (
          <p className="text-sm text-slate-500">No escribió nada.</p>
        )}
      </Seccion>

      <Seccion titulo="Lo que leyó Claude">
        <AnalisisDeClaude analisis={s.analisis} nombres={nombres} />
      </Seccion>

      {s.estado === 'ENVIADA' ? (
        <Decision s={s} elegibles={modulosParaCliente(catalogo)} />
      ) : s.estado === 'RECHAZADA' ? (
        <p className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
          Rechazada. Motivo: {s.motivoRechazo ?? '—'}
        </p>
      ) : s.estado === 'APROBADA' ? (
        <p className="flex items-center gap-2 rounded-md bg-emerald-50 p-3 text-sm text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
          <CheckCircle2 className="h-4 w-4 shrink-0" /> Aprobada: su negocio ya está en «Todos los clientes».
        </p>
      ) : null}
    </>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{titulo}</h3>
      {children}
    </section>
  );
}

/** El audio se pide con la sesión y se toca desde un enlace local que se suelta al salir. */
function Reproductor({ url }: { url: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    let local: string | null = null;
    api
      .audioDeSolicitud(url)
      .then((b) => {
        if (!vivo) return;
        local = URL.createObjectURL(b);
        setSrc(local);
      })
      .catch((e: Error) => vivo && setFallo(e.message));
    return () => {
      vivo = false;
      if (local) URL.revokeObjectURL(local);
    };
  }, [url]);

  if (fallo) return <p className="text-xs text-red-600">No se pudo cargar el audio: {fallo}</p>;
  if (!src) return <Spinner size={16} className="text-brand-600" />;
  return <audio controls preload="metadata" src={src} className="w-full" />;
}

/** Problema → solución → módulos, con la frase del cliente que lo justifica. */
export function AnalisisDeClaude({
  analisis,
  nombres,
}: {
  analisis: SolicitudDetalle['analisis'];
  nombres: Record<string, string>;
}) {
  if (!analisis) return <p className="text-sm text-slate-500">No hay análisis: revisa tú lo que contó.</p>;
  return (
    <div className="space-y-3">
      {analisis.resumen && <p className="text-sm text-slate-700 dark:text-slate-300">{analisis.resumen}</p>}
      <ul className="space-y-2">
        {analisis.hallazgos.map((h, i) => (
          <li key={i} className="space-y-1 rounded-md bg-slate-50 p-3 text-sm dark:bg-slate-800/50">
            <p>
              <span className="font-medium">Problema:</span> {h.problema}
            </p>
            <p>
              <span className="font-medium">Solución:</span> {h.solucion}
            </p>
            <p>
              <span className="font-medium">Módulos:</span> {h.modulos.map((m) => nombres[m] ?? m).join(', ')}
            </p>
            {h.frase && (
              <blockquote className="border-l-2 border-brand-400 pl-2 text-xs italic text-slate-600 dark:text-slate-400">
                «{h.frase}»
              </blockquote>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Decision({ s, elegibles }: { s: SolicitudDetalle; elegibles: ModuloDelCatalogo[] }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [nombre, setNombre] = useState(s.negocioNombre);
  const [slug, setSlug] = useState(aSlug(s.negocioNombre));
  const [marcados, setMarcados] = useState(() => marcadosAlAbrir(s.modulosPropuestos, elegibles));
  const [paso, setPaso] = useState<'elegir' | 'aprobar' | 'rechazar'>('elegir');
  const [motivo, setMotivo] = useState('');
  const [fallo, setFallo] = useState<string | null>(null);

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: QK });
    void qc.invalidateQueries({ queryKey: ['operacion', 'solicitud', s.id] });
    void qc.invalidateQueries({ queryKey: ['operacion', 'clientes'] });
  };

  const aprobar = useMutation({
    mutationFn: () => api.aprobarSolicitud(s.id, { nombre: nombre.trim(), slug, modulos: marcados }),
    onSuccess: () => {
      toast.success(`Aprobada: nació el negocio "${nombre.trim()}".`);
      void qc.invalidateQueries({ queryKey: ['workspaces'] });
      refrescar();
    },
    onError: (e: Error) => setFallo(e.message),
  });

  const rechazar = useMutation({
    mutationFn: () => api.rechazarSolicitud(s.id, motivo.trim()),
    onSuccess: () => {
      toast.success('Solicitud rechazada.');
      refrescar();
    },
    onError: (e: Error) => setFallo(e.message),
  });

  const alternar = (id: string) =>
    setMarcados((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
  const puedeAprobar = nombre.trim().length > 0 && slug.length >= 2 && marcados.length > 0;
  const ocupado = aprobar.isPending || rechazar.isPending;

  return (
    <section className="space-y-4 border-t border-surface-border pt-4 dark:border-slate-800">
      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Decidir</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Nombre del negocio" value={nombre} onChange={(e) => setNombre(e.target.value)} />
        <Input
          label="Identificador corto"
          className="font-mono"
          value={slug}
          hint="Minúsculas, números y guiones."
          onChange={(e) => setSlug(aSlug(e.target.value))}
        />
      </div>

      <fieldset className="space-y-2">
        <legend className="mb-1 text-sm font-medium text-slate-700 dark:text-slate-300">
          Módulos ({marcados.length}) — vienen marcados los que propuso
        </legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {elegibles.map((m) => (
            <label key={m.id} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={marcados.includes(m.id)}
                onChange={() => alternar(m.id)}
              />
              <span>
                {m.nombre}
                <span className="block text-xs text-slate-500">{m.descripcion}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {fallo && (
        <p className="rounded-md bg-red-50 p-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">{fallo}</p>
      )}

      {paso === 'elegir' && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => { setFallo(null); setPaso('aprobar'); }} disabled={!puedeAprobar}>
            <CheckCircle2 className="h-4 w-4" /> Aprobar
          </Button>
          <Button variant="outline" onClick={() => { setFallo(null); setPaso('rechazar'); }}>
            <XCircle className="h-4 w-4" /> Rechazar
          </Button>
        </div>
      )}

      {paso === 'aprobar' && (
        <div className="space-y-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900/50 dark:bg-emerald-900/20">
          <p className="text-sm text-slate-800 dark:text-slate-200">
            Se crea el negocio <strong>{nombre.trim()}</strong> (<span className="font-mono">{slug}</span>) con{' '}
            {marcados.length} {marcados.length === 1 ? 'módulo' : 'módulos'}, y {s.email} entra como su administrador.
            ¿Seguimos?
          </p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => aprobar.mutate()} disabled={!puedeAprobar || ocupado} loading={aprobar.isPending}>
              Sí, aprobar
            </Button>
            <Button variant="ghost" onClick={() => setPaso('elegir')} disabled={ocupado}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {paso === 'rechazar' && (
        <div className="space-y-3 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900/50 dark:bg-red-900/20">
          <Textarea
            label="Motivo del rechazo (lo verá el cliente)"
            rows={3}
            maxLength={2000}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="danger"
              onClick={() => rechazar.mutate()}
              disabled={!motivo.trim() || ocupado}
              loading={rechazar.isPending}
            >
              Rechazar solicitud
            </Button>
            <Button variant="ghost" onClick={() => setPaso('elegir')} disabled={ocupado}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

    </section>
  );
}
